import { FC, forwardRef, memo, useMemo } from 'react';

import { Typography as MaterialTypography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';

import { fontWeight } from '../../assets/theme/typography';
import { TypographyColor, TypographyProps, TypographyWeight } from './Typography.types';

/**
 * Get a color value from the palette using dot notation path.
 */
function getColorFromPalette(palette: Record<string, unknown>, colorPath: TypographyColor): string | undefined {
  const parts = colorPath.split('.');
  let current: unknown = palette;

  for (const part of parts) {
    if (current && typeof current === 'object' && part in current) {
      current = (current as Record<string, unknown>)[part];
    } else {
      return undefined;
    }
  }

  return typeof current === 'string' ? current : undefined;
}

/**
 * Map weight prop to CSS font-weight value.
 */
function getWeightValue(weight: TypographyWeight): string | number | undefined {
  return fontWeight[weight];
}

/**
 * @component Typography
 * @description A wrapper around Material UI Typography that provides type-safe access
 * to Oasis theme values for variant, color, size, and weight.
 *
 * @designSystem
 * - **Variants**: Use semantic variants (h1-h6 for headings, body1/body2 for paragraphs)
 * - **Colors**: Use palette colors for consistency (primary.main, text.secondary, etc.)
 * - **Sizes**: Prefer variant defaults; only override size when necessary
 * - **Weights**: light, normal, bold - matching Oasis typography theme
 *
 * @usage
 * - Use heading variants (h1-h6) for page structure and hierarchy
 * - Use body variants for paragraph text
 * - Use subtitle variants for secondary headings
 * - Use caption for small supporting text
 * - Use button variant for button-like text styling
 *
 * @accessibility
 * - Use semantic heading hierarchy (h1 > h2 > h3, etc.)
 * - Ensure adequate color contrast for text readability
 * - Don't skip heading levels
 *
 * @example
 * ```jsx
 * // Basic usage with variant
 * <Typography variant="h1">Page Title</Typography>
 *
 * // With custom color from palette
 * <Typography variant="body1" color="primary.main">Highlighted text</Typography>
 *
 * // With custom size and weight
 * <Typography variant="body2" size="16px" weight="bold">Bold custom size</Typography>
 *
 * // Error message styling
 * <Typography variant="caption" color="error.main">Error message</Typography>
 * ```
 */
export const Typography: FC<TypographyProps> = memo(
  forwardRef<HTMLElement, TypographyProps>(({ variant = 'body1', color, size, weight, style, ...props }, ref) => {
    const theme = useTheme();

    const computedStyle = useMemo(() => {
      const customStyles: React.CSSProperties = {};

      // Apply color from palette if specified
      if (color) {
        const colorValue = getColorFromPalette(theme.palette as unknown as Record<string, unknown>, color);
        if (colorValue) {
          customStyles.color = colorValue;
        }
      }

      // Apply custom size if specified
      if (size) {
        customStyles.fontSize = size;
      }

      // Apply custom weight if specified
      if (weight) {
        customStyles.fontWeight = getWeightValue(weight);
      }

      // Merge with any existing style prop
      return { ...customStyles, ...style };
    }, [color, size, weight, style, theme.palette]);

    return <MaterialTypography ref={ref} variant={variant} style={computedStyle} {...props} />;
  }),
);

Typography.displayName = 'Typography';
